import type { LookupTransferRequestResponse } from '@getstep/proto/transfer'
import { mustExist } from '@getstep/sdk/dist/util/Assert'
import { PersonalizationProvider } from '@lib/hooks/usePersonalizations'
import { useStores } from '@lib/store/useStores'
import { LINK } from '@shared/util/link'
import { useRouter } from 'next/router'
import type { FunctionComponent, PropsWithChildren } from 'react'
import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import useSWR from 'swr'

import { INTENT_STAGE_REGEX, PaymentIntentStage } from './PaymentIntentStage'
import { TransferRequestPageStore } from './TransferRequestPageStore'

/**
 * Gives you access to current page's TransferRequestPageStore.
 * Only works when there's valid transfer request and the page is wrapped in TransferRequestProvider
 */
export const useTransferRequestOptional = () =>
    useContext(TransferRequestContext)

export const useIntentStage = () => {
    const { pathname } = useRouter()
    return useMemo(
        () =>
            (pathname.match(INTENT_STAGE_REGEX) ?? [])[0] as
                | PaymentIntentStage
                | undefined,
        [pathname],
    )
}

/**
 * @see useTransferRequestOptional
 */
export const useTransferRequest = () =>
    mustExist(
        useTransferRequestOptional(),
        'Expected transfer request store to be defined',
    )

/**
 * @see useTransferRequestOptional
 */
export const usePaymentIntent = () =>
    mustExist(
        useTransferRequest().paymentIntent,
        'Expected paymentIntent to be defined',
    )

const TransferRequestContext = createContext<
    TransferRequestPageStore | undefined
>(undefined)

export const TransferRequestProvider: FunctionComponent<
    PropsWithChildren<LookupTransferRequestResponse>
> = ({ children, ...fallbackData }) => {
    const router = useRouter()
    const { sdk } = useStores()
    const id = router.query.tr as string
    const stage = useIntentStage()
    const [store, setStore] = useState(
        () => new TransferRequestPageStore(sdk, fallbackData),
    )

    const fetchRequest = useCallback(
        async (id: string) =>
            typeof id === 'string'
                ? TransferRequestPageStore.findTransferRequest(sdk, id)
                : undefined,
        [sdk],
    )

    const [refreshable, setRefreshable] = useState(() =>
        [
            undefined,
            PaymentIntentStage.Confirmation,
            PaymentIntentStage.Payment,
            PaymentIntentStage.Input,
        ].includes(stage),
    )

    // once we've navigated to loader, success, or error, we don't want to refresh anymore
    // since the api will not be returning any data
    // otherwise SWR will keep revalidating on mount, window focus, reconnect, route change, etc.
    useSWR(id, fetchRequest, {
        fallbackData,
        revalidateIfStale: refreshable,
        revalidateOnMount: refreshable,
        revalidateOnFocus: refreshable,
        revalidateOnReconnect: refreshable,
        onSuccess: async (newData) => {
            if (!newData) return

            if (newData.request?.id !== store.request?.id) {
                setRefreshable(true)
                setStore(new TransferRequestPageStore(sdk, newData))
            }

            if (refreshable && newData.status === 'EXPIRED') {
                // "request expired" modal is only shown if it expired while the user was paying
                setRefreshable(false)
                router.replace(
                    LINK.pay(id, router.query.flow as string).serialize(),
                )
            }
        },
    })

    return (
        <PersonalizationProvider value={store.personalizations}>
            <TransferRequestContext.Provider value={store}>
                {children}
            </TransferRequestContext.Provider>
        </PersonalizationProvider>
    )
}
